import React, { createContext, useContext, useMemo } from 'react';
import {
  translateTaxonomySlug,
  seedToMap,
  DCS_NAMESPACE,
  type TaxonomyMap,
  type TaxonomySeed,
  type DcsKey,
} from '@de/utils/taxonomy/fetchTaxonomy';

export interface TaxonomyContextValue {
  /** True once a taxonomy map (seed) is available. */
  ready: boolean;
  /** Resolve a slug to its label within a namespace; returns the slug on miss. */
  translate: (slug: string, namespace: string) => string;
  /** Convenience for the DCS triad — maps the field key to its namespace. */
  translateDcsValue: (key: DcsKey, slug: string) => string;
}

const identity: TaxonomyContextValue = {
  ready: false,
  translate: (slug) => slug,
  translateDcsValue: (_key, slug) => slug,
};

const TaxonomyContext = createContext<TaxonomyContextValue>(identity);

export interface TaxonomyProviderProps {
  children: React.ReactNode;
  /**
   * Pre-resolved taxonomy (SSR-prefetched), as a `Map` or serializable
   * entries array. When supplied, the provider is `ready` synchronously.
   * An empty / absent seed yields an identity resolver (translation = slug).
   */
  seed?: TaxonomyMap | TaxonomySeed;
}

const hasSeed = (seed?: TaxonomyMap | TaxonomySeed): boolean =>
  seed instanceof Map ? seed.size > 0 : Array.isArray(seed) && seed.length > 0;

/**
 * Provides a slug→label resolver for the DCS taxonomy, sourced from an
 * SSR-prefetched seed.
 *
 * Why seed-only (no fetch inside this provider): routes that jump past the DSL
 * entry step can prefetch on the server, while DynamicExperience can resolve a
 * seed before mounting this provider for entry flows with contextual DCS but no
 * bound Select. Keeping fetching above this provider makes its resolver ready
 * synchronously and avoids reinitializing DSM after mount, which could reset
 * dependent cascading Selects. Bound Select flows still let DSM own labels.
 */
export const TaxonomyProvider: React.FC<TaxonomyProviderProps> = ({
  children,
  seed,
}) => {
  const value = useMemo<TaxonomyContextValue>(() => {
    if (!hasSeed(seed)) return identity;
    const map = seedToMap(seed!);
    const translate = (slug: string, namespace: string) =>
      translateTaxonomySlug(map, slug, namespace);
    return {
      ready: true,
      translate,
      translateDcsValue: (key: DcsKey, slug: string) =>
        translate(slug, DCS_NAMESPACE[key]),
    };
  }, [seed]);

  return (
    <TaxonomyContext.Provider value={value}>
      {children}
    </TaxonomyContext.Provider>
  );
};

/**
 * Access the taxonomy resolver. Returns an identity resolver (translation =
 * slug) when no provider/seed is present, so consumers never need a null check.
 */
export function useTaxonomy(): TaxonomyContextValue {
  return useContext(TaxonomyContext);
}

export default TaxonomyProvider;
